import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, FileText, Image, ChevronDown, Loader2, Archive } from 'lucide-react';
import { useFlexEExport } from '@/hooks/useFlexEExport';

interface FlexEExportToolbarProps {
  simulationName: string;
  simulationData?: {
    contractLimit?: number;
    battKw?: number;
    battKwh?: number;
    windowStart?: string;
    windowEnd?: string;
    successRate?: number;
    baselineSuccessRate?: number;
  };
}

export default function FlexEExportToolbar({ simulationName, simulationData }: FlexEExportToolbarProps) {
  const {
    isExporting,
    exportProgress,
    downloadSingleChart,
    downloadAllChartsAsZip,
    downloadMethodology,
    chartConfigs,
  } = useFlexEExport();

  return (
    <div className="flex flex-wrap items-center gap-2 p-4 bg-muted/50 rounded-lg border">
      <span className="text-sm font-medium text-muted-foreground mr-2">
        Exporteren:
      </span>

      {/* Download All as ZIP */}
      <Button
        variant="default"
        size="sm"
        onClick={() => downloadAllChartsAsZip(simulationName)}
        disabled={isExporting}
      >
        {isExporting ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Archive className="w-4 h-4 mr-2" />
        )}
        {exportProgress || 'Alle grafieken (ZIP)'}
      </Button>

      {/* Methodology Download */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => downloadMethodology(simulationData)}
        disabled={isExporting}
      >
        <FileText className="w-4 h-4 mr-2" />
        Methodiek (.md)
      </Button>

      {/* Individual Chart Downloads */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={isExporting}>
            <Image className="w-4 h-4 mr-2" />
            Losse grafieken
            <ChevronDown className="w-4 h-4 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {chartConfigs.map((config) => (
            <DropdownMenuItem
              key={config.id}
              onClick={() => downloadSingleChart(config.id, config.filename)}
            >
              <Download className="w-4 h-4 mr-2" />
              {config.name}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => downloadAllChartsAsZip(simulationName)}
          >
            <Archive className="w-4 h-4 mr-2" />
            Alles downloaden (ZIP)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
